import { useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "@/i18n/translations";

const ALLOWED = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_BYTES = 2 * 1024 * 1024; // hard server cap
const MAX_SIDE = 1600;             // resize longer side down to this to fit under MAX_BYTES

/**
 * Resize a too-big image client-side via canvas → WebP. Keeps small/already-tiny
 * images untouched (returns the original File when no work is needed).
 */
async function maybeResize(file) {
  // Cheap path — small enough and not crazy huge resolution
  if (file.size <= MAX_BYTES && !/image\/(png|jpe?g)/i.test(file.type)) return file;

  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return file;

  const longest = Math.max(bitmap.width, bitmap.height);
  if (longest <= MAX_SIDE && file.size <= MAX_BYTES) {
    bitmap.close?.();
    return file;
  }

  const scale = Math.min(1, MAX_SIDE / longest);
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();

  // Try a few quality levels until under MAX_BYTES
  for (const q of [0.85, 0.75, 0.65, 0.55, 0.45]) {
    const blob = await new Promise((res) => canvas.toBlob(res, "image/webp", q));
    if (blob && blob.size <= MAX_BYTES) {
      return new File([blob], (file.name || "photo").replace(/\.[^.]+$/, "") + ".webp", { type: "image/webp" });
    }
  }
  // Fallback — return original; server will reject if still too big
  return file;
}

/**
 * Compact image uploader. Drag-n-drop OR click. Calls onUploaded(url) for each
 * successfully uploaded file. When `multi=true`, sends up to N files in
 * parallel and fires onUploaded for each.
 *
 * Usage:
 *   <ImageUploader API={API} onUploaded={(url) => setCoverUrl(url)} />
 *   <ImageUploader API={API} multi onUploaded={(url) => addPhoto(url)} />
 */
export default function ImageUploader({ API, onUploaded, multi = false, compact = false, maxFiles = null }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const validate = (file) => {
    if (!ALLOWED.includes(file.type)) {
      toast.error(t("uploader.invalidType"));
      return false;
    }
    // 50 MB safety cap on raw input — we'll resize anything between 2-50 MB on the client.
    if (file.size > 50 * 1024 * 1024) {
      toast.error(t("uploader.tooBig"));
      return false;
    }
    return true;
  };

  const uploadOne = async (file) => {
    const optimized = await maybeResize(file);
    // After resize the file may still exceed cap (extreme cases) — show a clearer error early.
    if (optimized.size > MAX_BYTES) {
      throw new Error(t("uploader.tooBig"));
    }
    const form = new FormData();
    form.append("file", optimized);
    const r = await axios.post(`${API}/uploads/image`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
    return r.data?.url;
  };

  const handleFiles = async (fileList) => {
    let files = Array.from(fileList || []).filter(validate);
    if (!files.length) return;
    // Respect the slot cap when the parent passes it (used to enforce gallery limit).
    if (typeof maxFiles === "number" && files.length > maxFiles) {
      files = files.slice(0, maxFiles);
      toast.info(t("uploader.trimmed").replace("{n}", String(maxFiles)));
    }
    setBusy(true);
    setProgress(0);
    try {
      if (multi) {
        for (const f of files) {
          // sequential — keeps progress simple and avoids storage rate-limits
          const url = await uploadOne(f);
          if (url) onUploaded(url);
        }
        toast.success(t("uploader.successMulti").replace("{n}", String(files.length)));
      } else {
        const url = await uploadOne(files[0]);
        if (url) onUploaded(url);
        toast.success(t("uploader.success"));
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "upload error";
      toast.error(`${t("uploader.failed")}: ${msg}`.slice(0, 160));
    } finally {
      setBusy(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  // ─── Compact mode: just a small button (used inline next to URL inputs) ──
  if (compact) {
    return (
      <>
        <input
          ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp"
          multiple={multi} className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="uploader-file-input"
        />
        <Button
          type="button" size="sm" variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="border-2 border-black text-black hover:bg-gray-50 whitespace-nowrap"
          data-testid="uploader-compact-btn"
        >
          {busy ? (
            <><Loader2 className="w-4 h-4 mr-1 animate-spin" />{progress}%</>
          ) : (
            <><Upload className="w-4 h-4 mr-1" />{t("uploader.btn")}</>
          )}
        </Button>
      </>
    );
  }

  // ─── Drop-zone mode: prominent dashed area ───────────────────────────────
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
      role="button" tabIndex={0}
      className={`cursor-pointer rounded-lg border-2 border-dashed p-4 text-center transition-colors ${
        dragOver ? "border-black bg-gray-100" : "border-gray-400 bg-gray-50 hover:bg-gray-100"
      } ${busy ? "opacity-60 pointer-events-none" : ""}`}
      data-testid="uploader-dropzone"
    >
      <input
        ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp"
        multiple={multi} className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={multi ? "uploader-file-input-multi" : "uploader-file-input"}
      />
      {busy ? (
        <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t("uploader.uploading")} — {progress}%
        </div>
      ) : (
        <div className="flex flex-col items-center gap-1 text-sm text-gray-700">
          <Upload className="w-5 h-5" />
          <span className="font-medium">{multi ? t("uploader.dropzoneMulti") : t("uploader.dropzone")}</span>
          <span className="text-xs text-gray-500">{t("uploader.hint")}</span>
        </div>
      )}
    </div>
  );
}
